import React, { useState, useEffect } from 'react';
import api from '../api';
import Layout from './Layout';

// ============================================
// API FUNCTIONS
// ============================================

const fetchStudentFilters = async (tenantId) => {
  try {
    const response = await api.get('/api/head-of-institution/students/filters', {
      headers: { 
        'x-tenant-id': tenantId 
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching filters:', error);
    throw error;
  }
};

const fetchStudents = async (tenantId, filters) => {
  try {
    const response = await api.get('/api/head-of-institution/students', {
      params: filters,
      headers: { 
        'x-tenant-id': tenantId 
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching students:', error);
    throw error;
  }
};

const fetchStudentDetails = async (tenantId, studentId, filters) => {
  try {
    const response = await api.get(`/api/head-of-institution/students/${studentId}`, {
      params: filters,
      headers: { 
        'x-tenant-id': tenantId 
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching student details:', error);
    throw error;
  }
};

// ============================================
// HELPER FUNCTIONS
// ============================================

const getStatusColor = (status) => {
  if (!status) return 'bg-slate-100 text-slate-700';
  const colors = {
    active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    inactive: 'bg-rose-50 text-rose-700 border-rose-200',
    pending: 'bg-amber-50 text-amber-700 border-amber-200',
    suspended: 'bg-orange-50 text-orange-700 border-orange-200'
  };
  return colors[status.toLowerCase()] || 'bg-slate-50 text-slate-700 border-slate-200';
};

const getEligibilityColor = (isEligible) => {
  return isEligible 
    ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' 
    : 'bg-rose-50 text-rose-700 border border-rose-200';
};

const getPercentageColor = (percentage) => {
  if (percentage >= 75) return 'text-emerald-600 font-semibold';
  if (percentage >= 50) return 'text-amber-600 font-semibold';
  return 'text-rose-600 font-semibold';
};

const getGradeBadge = (percentage) => {
  if (percentage >= 90) return { label: 'O', color: 'bg-purple-50 text-purple-700 border-purple-200' };
  if (percentage >= 80) return { label: 'A+', color: 'bg-blue-50 text-blue-700 border-blue-200' };
  if (percentage >= 70) return { label: 'A', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
  if (percentage >= 60) return { label: 'B+', color: 'bg-teal-50 text-teal-700 border-teal-200' };
  if (percentage >= 50) return { label: 'B', color: 'bg-amber-50 text-amber-700 border-amber-200' };
  if (percentage >= 45) return { label: 'C', color: 'bg-orange-50 text-orange-700 border-orange-200' };
  if (percentage >= 40) return { label: 'P', color: 'bg-yellow-50 text-yellow-700 border-yellow-200' };
  return { label: 'F', color: 'bg-rose-50 text-rose-700 border-rose-200' };
};

const getAssessmentTypeLabel = (type) => {
  const labels = {
    'internal_only': 'Internal Only',
    'external_only': 'External Only',
    'both': 'Both'
  };
  return labels[type] || type;
};

const getAssessmentTypeBadge = (type) => {
  const colors = {
    'internal_only': 'bg-blue-50 text-blue-700 border-blue-200',
    'external_only': 'bg-orange-50 text-orange-700 border-orange-200',
    'both': 'bg-purple-50 text-purple-700 border-purple-200'
  };
  return colors[type] || 'bg-slate-50 text-slate-700 border-slate-200';
};

// Helper to reliably parse image paths from student information models
const parseProfilePhotoUrl = (personalInfo) => {
  if (!personalInfo) return null;
  let rawUrl = personalInfo.profilePhotoUrl || personalInfo.profilePhoto?.url || personalInfo.profilePhoto;
  
  if (!rawUrl || typeof rawUrl !== 'string') return null;
  
  if (rawUrl.startsWith('http://') || rawUrl.startsWith('https://')) {
    return rawUrl;
  }
  
  const baseURL = api.defaults.baseURL || '';
  return `${baseURL}${rawUrl}`;
};

// ============================================
// STUDENT DETAILS MODAL
// ============================================
const StudentDetailsModal = ({ student, onClose }) => {
  const [resolvedImageUrl, setResolvedImageUrl] = useState(null);
  const [imageLoading, setImageLoading] = useState(true);

  useEffect(() => {
    const verifyImageAvailability = async () => {
      if (!student?.personalInfo) {
        setImageLoading(false);
        return;
      }

      const fullUrl = parseProfilePhotoUrl(student.personalInfo);

      if (!fullUrl) {
        setResolvedImageUrl(null);
        setImageLoading(false);
        return;
      }

      // If it's already an absolute external link, bypass head check optimizations
      if (fullUrl.startsWith('http://') || fullUrl.startsWith('https://')) {
        setResolvedImageUrl(fullUrl);
        setImageLoading(false);
        return;
      }

      try {
        await api.head(fullUrl);
        setResolvedImageUrl(fullUrl);
      } catch {
        setResolvedImageUrl(null);
      } finally {
        setImageLoading(false);
      }
    };

    verifyImageAvailability();
  }, [student]);

  if (!student) return null;

  const { personalInfo, academicInfo, parentInfo, subjectPerformance, overallStats } = student;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/60 backdrop-blur-sm p-0 sm:p-4" onClick={onClose}>
      <div 
        className="relative w-full max-w-4xl bg-white rounded-t-2xl sm:rounded-2xl shadow-xl max-h-[92vh] sm:max-h-[90vh] overflow-y-auto animate-in slide-in-from-bottom duration-200" 
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header inside Modal */}
        <div className="sticky top-0 z-10 bg-gradient-to-r from-slate-900 to-indigo-950 px-5 py-5 text-white flex items-center justify-between border-b border-white/10 shadow-sm">
          <div className="flex items-center gap-3.5">
            {imageLoading ? (
              <div className="h-14 w-14 rounded-xl bg-white/20 animate-pulse border border-white/30" />
            ) : resolvedImageUrl ? (
              <img 
                src={resolvedImageUrl} 
                alt={personalInfo.fullName}
                className="h-14 w-14 rounded-xl object-cover ring-2 ring-white/20"
                onError={() => setResolvedImageUrl(null)}
              />
            ) : (
              <div className="h-14 w-14 rounded-xl bg-white/10 flex items-center justify-center text-xl font-bold border border-white/20">
                {personalInfo.fullName?.charAt(0) || '?'}
              </div>
            )}
            <div>
              <h3 className="text-lg font-bold leading-tight">{personalInfo.fullName}</h3>
              <p className="text-xs text-slate-300 font-mono mt-0.5">{personalInfo.usn}</p>
            </div>
          </div>
          
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-4 sm:p-6 space-y-6">
          {/* Top Info Strip */}
          <div className="flex flex-wrap gap-2 bg-slate-50 p-3 rounded-xl border border-slate-200">
            <span className="text-xs text-slate-500 flex items-center gap-1.5 mr-2">
              <span className="h-1.5 w-1.5 rounded-full bg-slate-400"></span>
              Status:
            </span>
            <span className={`inline-flex items-center text-xs font-semibold px-2.5 py-0.5 rounded-md border ${getStatusColor(personalInfo.status)}`}>
              {personalInfo.status || 'Inactive'}
            </span>
            <span className="inline-flex items-center text-xs font-medium px-2.5 py-0.5 rounded-md bg-slate-100 text-slate-700 border border-slate-200 truncate max-w-[200px]">
              {personalInfo.email}
            </span>
          </div>

          {/* Personal & Academic Info Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
              <h4 className="text-xs font-bold uppercase tracking-wider text-indigo-600 mb-3 flex items-center gap-1.5">
                👤 Personal Details
              </h4>
              <div className="space-y-2.5 text-xs sm:text-sm divide-y divide-slate-100">
                <div className="flex justify-between pt-2 first:pt-0">
                  <span className="text-slate-500">Phone</span>
                  <span className="font-medium text-slate-800">{personalInfo.phone || 'N/A'}</span>
                </div>
                <div className="flex justify-between pt-2">
                  <span className="text-slate-500">Date of Birth</span>
                  <span className="font-medium text-slate-800">{personalInfo.dateOfBirth || 'N/A'}</span>
                </div>
                <div className="flex justify-between pt-2">
                  <span className="text-slate-500">Gender</span>
                  <span className="font-medium text-slate-800 capitalize">{personalInfo.gender || 'N/A'}</span>
                </div>
                <div className="flex justify-between pt-2">
                  <span className="text-slate-500">Blood Group</span>
                  <span className="font-medium text-slate-800">{personalInfo.bloodGroup || 'N/A'}</span>
                </div>
                <div className="flex justify-between pt-2">
                  <span className="text-slate-500">Nationality</span>
                  <span className="font-medium text-slate-800">{personalInfo.nationality || 'N/A'}</span>
                </div>
                <div className="flex justify-between pt-2">
                  <span className="text-slate-500">Category</span>
                  <span className="font-medium text-slate-800">{personalInfo.category || 'N/A'}</span>
                </div>
                <div className="flex justify-between pt-2">
                  <span className="text-slate-500">Address</span>
                  <span className="font-medium text-slate-800 text-right max-w-[60%] truncate">{personalInfo.address || 'N/A'}</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
              <h4 className="text-xs font-bold uppercase tracking-wider text-indigo-600 mb-3 flex items-center gap-1.5">
                🎓 Academic Profile
              </h4>
              <div className="space-y-2.5 text-xs sm:text-sm divide-y divide-slate-100">
                <div className="flex justify-between pt-2 first:pt-0">
                  <span className="text-slate-500">Program</span>
                  <span className="font-medium text-slate-800 text-right">{academicInfo.programName} ({academicInfo.programCode})</span>
                </div>
                <div className="flex justify-between pt-2">
                  <span className="text-slate-500">Program Type</span>
                  <span className="font-medium text-slate-800">{academicInfo.programTypeName}</span>
                </div>
                <div className="flex justify-between pt-2">
                  <span className="text-slate-500">Academic Year</span>
                  <span className="font-medium text-slate-800">{academicInfo.academicYearName}</span>
                </div>
                <div className="flex justify-between pt-2">
                  <span className="text-slate-500">Class</span>
                  <span className="font-medium text-slate-800">{academicInfo.className}</span>
                </div>
                <div className="flex justify-between pt-2">
                  <span className="text-slate-500">{academicInfo.isSemesterBased ? 'Semester' : 'Term'}</span>
                  <span className="font-medium text-slate-800">{academicInfo.termName || academicInfo.semesterName || 'N/A'}</span>
                </div>
                <div className="flex justify-between pt-2">
                  <span className="text-slate-500">Admission No.</span>
                  <span className="font-medium text-slate-800 font-mono">{personalInfo.admissionNumber || 'N/A'}</span>
                </div>
                <div className="flex justify-between pt-2">
                  <span className="text-slate-500">Admission Date</span>
                  <span className="font-medium text-slate-800">
                    {academicInfo.admissionDate ? new Date(academicInfo.admissionDate).toLocaleDateString() : 'N/A'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Parent Info */}
          <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
            <h4 className="text-xs font-bold uppercase tracking-wider text-indigo-600 mb-3 flex items-center gap-1.5">
              👨‍👩‍👦 Parent / Guardian Details
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs sm:text-sm">
              <div className="bg-slate-50 p-2.5 rounded-lg border border-slate-100">
                <span className="text-slate-500 block text-[11px] uppercase tracking-wider font-semibold">Name</span>
                <p className="font-semibold text-slate-800 mt-0.5">{parentInfo.parentName || 'N/A'}</p>
              </div>
              <div className="bg-slate-50 p-2.5 rounded-lg border border-slate-100">
                <span className="text-slate-500 block text-[11px] uppercase tracking-wider font-semibold">Email</span>
                <p className="font-semibold text-slate-800 mt-0.5 truncate">{parentInfo.parentEmail || 'N/A'}</p>
              </div>
              <div className="bg-slate-50 p-2.5 rounded-lg border border-slate-100">
                <span className="text-slate-500 block text-[11px] uppercase tracking-wider font-semibold">Phone</span>
                <p className="font-semibold text-slate-800 mt-0.5">{parentInfo.parentPhone || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Subject Performance */}
          <div>
            <h4 className="text-xs font-bold uppercase tracking-wider text-indigo-600 mb-3 flex items-center gap-1.5">
              📊 Course Performances
            </h4>
            
            {/* Mobile View Card List */}
            <div className="space-y-3 block lg:hidden">
              {subjectPerformance.map((sub, idx) => {
                const assessmentType = sub.assessmentType || 'both';
                const displayTotal = sub.displayTotal || { score: 0, maxMarks: 0, percentage: 0 };
                const grade = getGradeBadge(displayTotal.percentage);
                return (
                  <div key={idx} className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm space-y-2.5">
                    <div className="flex justify-between items-start gap-2">
                      <div>
                        <h5 className="font-bold text-slate-800 text-sm">
                          {sub.subjectName}
                        </h5>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {sub.isElective && <span className="bg-purple-50 text-purple-700 text-[10px] px-1.5 py-0.5 rounded border border-purple-200 font-medium">Elective</span>}
                          {sub.isLab && <span className="bg-blue-50 text-blue-700 text-[10px] px-1.5 py-0.5 rounded border border-blue-200 font-medium">Lab</span>}
                          <span className={`text-[10px] px-1.5 py-0.5 rounded border font-medium ${getAssessmentTypeBadge(assessmentType)}`}>
                            {getAssessmentTypeLabel(assessmentType)}
                          </span>
                        </div>
                      </div>
                      <span className={`px-2 py-0.5 text-xs font-semibold rounded border ${getEligibilityColor(sub.eligibility.isOverallEligible)}`}>
                        {sub.eligibility.isOverallEligible ? 'Eligible' : 'Not Eligible'}
                      </span>
                    </div>

                    <div className="grid grid-cols-3 gap-2 bg-slate-50 p-2.5 rounded-lg text-center text-xs border border-slate-100">
                      <div>
                        <span className="text-slate-400 block text-[10px]">Attendance</span>
                        <span className={`mt-0.5 block ${getPercentageColor(sub.attendance.percentage)}`}>
                          {sub.attendance.percentage.toFixed(1)}%
                        </span>
                      </div>
                      <div>
                        <span className="text-slate-400 block text-[10px]">Total Score</span>
                        <span className="font-bold text-slate-800 mt-0.5 block">
                          {displayTotal.maxMarks > 0 ? `${displayTotal.score}/${displayTotal.maxMarks}` : 'N/A'}
                        </span>
                      </div>
                      <div>
                        <span className="text-slate-400 block text-[10px]">Grade</span>
                        <span className={`inline-block mt-0.5 px-2 py-0.2 text-[11px] font-bold rounded border ${displayTotal.maxMarks > 0 ? grade.color : 'bg-slate-100 text-slate-400'}`}>
                          {displayTotal.maxMarks > 0 ? grade.label : '-'}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Desktop Table View */}
            <div className="hidden lg:block overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-900 border-b border-slate-800 text-white">
                    <th className="px-4 py-3 text-left font-semibold text-xs uppercase tracking-wider">Subject</th>
                    <th className="px-3 py-3 text-center font-semibold text-xs uppercase tracking-wider">Type</th>
                    <th className="px-3 py-3 text-center font-semibold text-xs uppercase tracking-wider">Attendance</th>
                    <th className="px-3 py-3 text-center font-semibold text-xs uppercase tracking-wider">CIE Marks</th>
                    <th className="px-3 py-3 text-center font-semibold text-xs uppercase tracking-wider">End Exam</th>
                    <th className="px-3 py-3 text-center font-semibold text-xs uppercase tracking-wider">Total Marks</th>
                    <th className="px-3 py-3 text-center font-semibold text-xs uppercase tracking-wider">Grade</th>
                    <th className="px-4 py-3 text-center font-semibold text-xs uppercase tracking-wider">Eligible</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {subjectPerformance.map((sub, idx) => {
                    const assessmentType = sub.assessmentType || 'both';
                    const displayTotal = sub.displayTotal || { score: 0, maxMarks: 0, percentage: 0 };
                    const cieHasData = sub.cieMarks?.hasData || false;
                    const endExamHasData = sub.endExamMarks?.hasData || false;
                    const grade = getGradeBadge(displayTotal.percentage);
                    
                    const showCIE = assessmentType === 'internal_only' || assessmentType === 'both';
                    const showEndExam = assessmentType === 'external_only' || assessmentType === 'both';
                    
                    let cieDisplay = 'N/A';
                    if (showCIE) {
                      if (cieHasData && sub.cieMarks) {
                        const cieScore = sub.cieMarks.totalCIEScore || 0;
                        const cieMax = sub.cieMarks.cieMaxMarks || 0;
                        cieDisplay = `${cieScore}/${cieMax}`;
                      } else {
                        cieDisplay = 'Not Entered';
                      }
                    }
                    
                    let endExamDisplay = 'N/A';
                    if (showEndExam) {
                      if (endExamHasData && sub.endExamMarks) {
                        const endScore = sub.endExamMarks.contributionToFinal || 0;
                        const endMax = sub.endExamMarks.endExamMaxMarks || sub.endExamMarks.totalEndExamMaxMarks || 0;
                        endExamDisplay = `${endScore}/${endMax}`;
                      } else {
                        endExamDisplay = 'Not Entered';
                      }
                    }
                    
                    let totalDisplay = 'Not Entered';
                    if (displayTotal.maxMarks > 0) {
                      totalDisplay = `${displayTotal.score}/${displayTotal.maxMarks}`;
                    }

                    return (
                      <tr key={idx} className="hover:bg-slate-50 transition-colors">
                        <td className="px-4 py-3 font-medium text-slate-800">
                          <div className="font-semibold text-slate-900">{sub.subjectName}</div>
                          <div className="flex gap-1 mt-0.5">
                            {sub.isElective && <span className="text-[10px] bg-purple-50 text-purple-700 px-1 rounded border border-purple-200">Elective</span>}
                            {sub.isLab && <span className="text-[10px] bg-blue-50 text-blue-700 px-1 rounded border border-blue-200">Lab</span>}
                          </div>
                        </td>
                        <td className="px-3 py-3 text-center">
                          <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-md border ${getAssessmentTypeBadge(assessmentType)}`}>
                            {getAssessmentTypeLabel(assessmentType)}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-center">
                          <span className={getPercentageColor(sub.attendance.percentage)}>
                            {sub.attendance.percentage.toFixed(1)}%
                          </span>
                        </td>
                        <td className="px-3 py-3 text-center text-xs font-medium text-slate-700">
                          {cieDisplay}
                        </td>
                        <td className="px-3 py-3 text-center text-xs font-medium text-slate-700">
                          {endExamDisplay}
                        </td>
                        <td className="px-3 py-3 text-center">
                          <div className="font-semibold text-slate-900">{totalDisplay}</div>
                          {displayTotal.maxMarks > 0 && (
                            <span className="text-[11px] text-slate-400">({displayTotal.percentage.toFixed(1)}%)</span>
                          )}
                        </td>
                        <td className="px-3 py-3 text-center">
                          {displayTotal.maxMarks > 0 ? (
                            <span className={`inline-block px-2 py-0.5 text-xs font-bold rounded-md border ${grade.color}`}>
                              {grade.label}
                            </span>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`inline-block px-2.5 py-0.5 text-xs font-semibold rounded-md border ${getEligibilityColor(sub.eligibility.isOverallEligible)}`}>
                            {sub.eligibility.isOverallEligible ? '✅ Yes' : '❌ No'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Component Breakdown */}
          {subjectPerformance.some(sub => sub.cieMarks?.componentScores?.length > 0) && (
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wider text-indigo-600 mb-3 flex items-center gap-1.5">
                📝 Component-wise Internal Breakdown
              </h4>
              <div className="space-y-3">
                {subjectPerformance.map((sub, idx) => {
                  if (!sub.cieMarks?.componentScores?.length) return null;
                  return (
                    <div key={idx} className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
                      <h5 className="text-xs font-bold text-slate-800 mb-3 pb-2 border-b border-slate-100 flex items-center justify-between">
                        <span>{sub.subjectName}</span>
                        <span className="text-[11px] text-slate-400 font-normal">Internal Evaluation Details</span>
                      </h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {sub.cieMarks.componentScores.map((comp, cIdx) => (
                          <div key={cIdx} className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="text-xs font-bold text-slate-800">{comp.componentName}</p>
                                <p className="text-[10px] text-slate-400 mt-0.5">
                                  {comp.aggregationRuleApplied} 
                                  {comp.nValueUsed && ` (n=${comp.nValueUsed})`}
                                </p>
                              </div>
                              <span className={`text-xs ${getPercentageColor(comp.normalizedScore || 0)}`}>
                                {comp.normalizedScore?.toFixed(1) || 0}%
                              </span>
                            </div>
                            <div className="mt-2 text-[11px] text-slate-600 bg-white px-2 py-1 rounded border border-slate-100 flex flex-wrap gap-x-3 gap-y-1">
                              <span><strong>Score:</strong> {comp.rawScore || 0}/{comp.maxRawScore || 0}</span>
                              {comp.weightageInCIE && <span><strong>Weight:</strong> {comp.weightageInCIE}%</span>}
                              {comp.contributionToCIE && <span><strong>Contribution:</strong> {comp.contributionToCIE.toFixed(2)}</span>}
                            </div>
                            {comp.itemsIncluded?.length > 0 && (
                              <div className="mt-2.5 pt-2 border-t border-slate-200/60">
                                <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-1">Items Evaluation</p>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                                  {comp.itemsIncluded.map((item, iIdx) => (
                                    <div key={iIdx} className="flex justify-between items-center text-[11px] bg-white px-2 py-1 rounded border border-slate-100">
                                      <span className="text-slate-500 truncate max-w-[70%]">{item.itemName || `Item ${iIdx + 1}`}</span>
                                      <span className="font-semibold text-slate-700 shrink-0">
                                        {item.rawScore || 0}/{item.maxRawScore || 0}
                                        {item.isSelected && <span className="ml-1 text-emerald-600 font-bold">✓</span>}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* End Exam Breakdown */}
          {subjectPerformance.some(sub => sub.endExamMarks?.itemScores?.length > 0) && (
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wider text-indigo-600 mb-3 flex items-center gap-1.5">
                🏛️ End Semester Exam Breakdown
              </h4>
              <div className="space-y-3">
                {subjectPerformance.map((sub, idx) => {
                  if (!sub.endExamMarks?.itemScores?.length) return null;
                  return (
                    <div key={idx} className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
                      <h5 className="text-xs font-bold text-slate-800 mb-3 pb-2 border-b border-slate-100">
                        {sub.subjectName}
                      </h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {sub.endExamMarks.itemScores.map((item, iIdx) => (
                          <div key={iIdx} className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="text-xs font-bold text-slate-800">
                                  {item.itemName || `End Exam ${iIdx + 1}`}
                                </p>
                                <span className={`inline-block mt-0.5 text-[10px] px-1.5 py-0.2 font-medium rounded ${item.isSelected ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' : 'bg-slate-100 text-slate-400'}`}>
                                  {item.isSelected ? 'Selected ✓' : 'Not Selected'}
                                </span>
                              </div>
                              <span className={`text-xs ${getPercentageColor(item.percentage || 0)}`}>
                                {item.percentage?.toFixed(1) || 0}%
                              </span>
                            </div>
                            <div className="mt-2 text-[11px] text-slate-600 bg-white px-2 py-1 rounded border border-slate-100 flex gap-3">
                              <span><strong>Score:</strong> {item.rawScore || 0}/{item.maxRawScore || 0}</span>
                              {item.contributionToEndExam && <span><strong>Contribution:</strong> {item.contributionToEndExam.toFixed(2)}</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      {sub.endExamMarks.isComplete && (
                        <div className="mt-3 text-xs text-slate-700 bg-slate-900 text-white rounded-lg p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 shadow-inner">
                          <div>
                            <span className="text-slate-400">Final End Exam Score Summary:</span>
                            <span className="ml-1.5 font-bold">
                              {sub.endExamMarks.contributionToFinal || 0}/{sub.endExamMarks.endExamMaxMarks || sub.endExamMarks.totalEndExamMaxMarks || 0} 
                              <span className="text-slate-300 font-normal ml-1">({sub.endExamMarks.endExamPercentage?.toFixed(1) || 0}%)</span>
                            </span>
                          </div>
                          <span className="text-[11px] bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 px-2 py-0.5 rounded font-semibold self-start sm:self-auto">
                            {sub.endExamMarks.isComplete ? '✅ Complete' : '⏳ Incomplete'}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Overall Stats */}
          <div className="bg-gradient-to-r from-indigo-900 to-slate-950 text-white rounded-xl p-4 sm:p-5 border border-slate-800 shadow-md">
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-300 mb-4 flex items-center gap-1.5">
              🏆 Performance Summary
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 text-center">
              <div className="bg-white/5 p-3 rounded-lg border border-white/10">
                <p className="text-[10px] uppercase tracking-wider text-slate-400">Total Subjects</p>
                <p className="text-xl font-bold mt-1 text-white">{overallStats.totalSubjects}</p>
              </div>
              <div className="bg-white/5 p-3 rounded-lg border border-white/10">
                <p className="text-[10px] uppercase tracking-wider text-slate-400">Eligible Subjects</p>
                <p className="text-xl font-bold mt-1 text-emerald-400">{overallStats.eligibleSubjects}</p>
              </div>
              <div className="bg-white/5 p-3 rounded-lg border border-white/10">
                <p className="text-[10px] uppercase tracking-wider text-slate-400">Attendance</p>
                <p className="text-xl font-bold mt-1 text-blue-300">
                  {overallStats.overallAttendancePercentage.toFixed(1)}%
                </p>
              </div>
              <div className="bg-white/5 p-3 rounded-lg border border-white/10 col-span-1">
                <p className="text-[10px] uppercase tracking-wider text-slate-400">Eligibility</p>
                <span className={`inline-block mt-1.5 px-2 py-0.5 text-xs font-bold rounded ${overallStats.overallEligible ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30' : 'bg-rose-500/20 text-rose-300 border border-rose-500/30'}`}>
                  {overallStats.overallEligible ? 'Eligible' : 'Not Eligible'}
                </span>
              </div>
              <div className="bg-white/5 p-3 rounded-lg border border-white/10 col-span-1">
                <p className="text-[10px] uppercase tracking-wider text-slate-400">Final Grade</p>
                <span className="inline-block mt-1.5 px-2.5 py-0.5 text-xs font-bold rounded bg-purple-500/20 text-purple-300 border border-purple-500/30">
                  {getGradeBadge(overallStats.overallAttendancePercentage || 0).label}
                </span>
              </div>
            </div>
            {overallStats.eligibilityReason && (
              <div className="mt-4 text-xs text-slate-300 bg-black/20 p-3 rounded-lg border border-white/5 flex items-start gap-2">
                <span className="shrink-0 mt-0.5">ℹ️</span>
                <p className="leading-relaxed"><strong className="text-white">Reason:</strong> {overallStats.eligibilityReason}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// LOADING COMPONENT
// ============================================
const StudentsLoading = () => (
  <div className="w-full rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden p-4 max-w-7xl mx-auto space-y-4">
    <div className="h-10 w-44 rounded bg-slate-200 animate-pulse mb-6" />
    <div className="h-24 w-full rounded-xl bg-slate-900 animate-pulse" />
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mt-6">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="h-14 rounded-lg bg-slate-100 animate-pulse" />
      ))}
    </div>
  </div>
);

// ============================================
// MAIN COMPONENT
// ============================================
const Students = () => {
  
  // ============ ACADEMIC CONTEXT SELECTION STATE ============
  const [tenantId, setTenantId] = useState('');
  const [programTypeId, setProgramTypeId] = useState('');
  const [programId, setProgramId] = useState('');
  const [academicYearId, setAcademicYearId] = useState('');
  const [semesterId, setSemesterId] = useState('');
  const [termId, setTermId] = useState('');
  
  // ============ SEARCH FILTERS STATE ============
  const [searchQuery, setSearchQuery] = useState('');
  
  // ============ DROPDOWN OPTIONS STATE ============
  const [programTypes, setProgramTypes] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [selectedAcademicYear, setSelectedAcademicYear] = useState(null);
  const [filteredPrograms, setFilteredPrograms] = useState([]);
  const [loadingFilters, setLoadingFilters] = useState(false);
  const [filterError, setFilterError] = useState('');
  
  // ============ STUDENTS STATE ============
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [contextSummary, setContextSummary] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [currentPage, setCurrentPage] = useState(1);
  
  // ============ STUDENT MODAL STATE ============
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [studentDetailsLoading, setStudentDetailsLoading] = useState(false);

  // ============ LOAD TENANT ID ============
  useEffect(() => {
    const tid = localStorage.getItem('tenantId');
    if (tid) {
      console.log('Tenant ID loaded:', tid);
      setTenantId(tid);
    } else {
      console.warn('No tenantId found in localStorage');
    }
  }, []);

  // ============ FETCH DROPDOWN OPTIONS ============
  useEffect(() => {
    if (!tenantId) {
      console.log('No tenantId available yet, waiting...');
      return;
    }
    
    const loadFilters = async () => {
      setLoadingFilters(true);
      setFilterError('');
      
      try {
        const response = await fetchStudentFilters(tenantId);
        
        if (response.success) {
          setAcademicYears(response.data.academicYears || []);
          setProgramTypes(response.data.programTypes || []);
          setPrograms(response.data.programs || []);
        } else {
          setFilterError(response.message || 'Failed to load filters');
        }
      } catch (err) {
        console.error('Error fetching filters:', err);
        if (err.response?.status === 401) {
          setFilterError('Login session expired. Please login again.');
        } else if (err.response?.status === 403) {
          setFilterError('You do not have permission to view this page.');
        } else {
          setFilterError(err.response?.data?.message || err.message || 'Failed to load filters');
        }
      } finally {
        setLoadingFilters(false);
      }
    };
    
    loadFilters();
  }, [tenantId]);

  // ============ FILTER PROGRAMS BY PROGRAM TYPE ============
  useEffect(() => {
    if (programTypeId) {
      setFilteredPrograms(programs.filter(p => p.programTypeId === programTypeId));
    } else {
      setFilteredPrograms(programs);
    }
  }, [programTypeId, programs]);

  // ============ FETCH STUDENTS WHEN CONTEXT CHANGES ============
  useEffect(() => {
    if (!tenantId || !programTypeId || !programId || !academicYearId) {
      setStudents([]);
      return;
    }

    const selectedAY = academicYears.find(y => y._id === academicYearId);
    if (!selectedAY) return;

    const hasPeriod = (selectedAY.isSemesterBased && semesterId) || 
                      (!selectedAY.isSemesterBased && termId);
    
    if (!hasPeriod) {
      setStudents([]);
      return;
    }

    const filters = {
      programTypeId,
      programId,
      academicYearId,
      page: currentPage,
      limit: 20
    };

    if (selectedAY.isSemesterBased && semesterId) {
      filters.semesterId = semesterId;
    } else if (!selectedAY.isSemesterBased && termId) {
      filters.termId = termId;
    }

    setLoading(true);
    setError('');

    const programTypeName = programTypes.find(pt => pt._id === programTypeId)?.name || '';
    const programName = programs.find(p => p._id === programId)?.name || '';
    const termName = selectedAY.isSemesterBased 
      ? selectedAY.semesters?.find(s => s._id === semesterId)?.semesterName 
      : selectedAY.terms?.find(t => t._id === termId)?.termLabel;

    fetchStudents(tenantId, filters)
      .then(response => {
        if (response.success) {
          // SORT STUDENTS BY USN ALPHABETICALLY BEFORE SAVING TO STATE
          const sortedList = (response.data.students || []).sort((a, b) => {
            const usnA = a.personalInfo?.usn || '';
            const usnB = b.personalInfo?.usn || '';
            return usnA.localeCompare(usnB);
          });
          
          setStudents(sortedList);
          setPagination(response.data.pagination || { page: 1, limit: 20, total: 0, pages: 0 });
          setContextSummary({
            programTypeName,
            programName,
            academicYearName: selectedAY.name || '',
            termName: termName || 'N/A',
            isSemesterBased: selectedAY.isSemesterBased,
            totalStudents: response.data.totalStudents || 0,
            eligibleStudents: response.data.summary?.eligibleStudents || 0,
            averageAttendance: response.data.summary?.averageAttendance || 0,
            totalSubjects: response.data.summary?.totalSubjects || 0
          });
        } else {
          setError(response.message || 'Failed to load students');
        }
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching students:', err);
        setError(err.response?.data?.message || 'Failed to load students');
        setLoading(false);
      });
  }, [tenantId, programTypeId, programId, academicYearId, semesterId, termId, currentPage, academicYears, programTypes, programs]);

  // ============ VIEW STUDENT DETAILS ============
  const handleViewStudent = async (student) => {
    setStudentDetailsLoading(true);
    setSelectedStudent(student);
    setIsModalOpen(true);
    
    try {
      const filters = {
        academicYearId,
        programTypeId,
        programId,
        ...(semesterId && { semesterId }),
        ...(termId && { termId }),
        includeHistory: 'true'
      };
      
      const response = await fetchStudentDetails(tenantId, student.studentId, filters);
      if (response.success && response.data.students?.length > 0) {
        setSelectedStudent(response.data.students[0]);
      }
    } catch (err) {
      console.error('Failed to fetch student details:', err);
    } finally {
      setStudentDetailsLoading(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedStudent(null);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.pages) {
      setCurrentPage(newPage);
    }
  };

  // ============ LOCAL FILTERING LOGIC (SEARCH) ============
  const filteredStudents = students.filter(student => {
    const query = searchQuery.toLowerCase().trim();
    if (!query) return true;
    
    const nameMatch = student.personalInfo?.fullName?.toLowerCase().includes(query);
    const usnMatch = student.personalInfo?.usn?.toLowerCase().includes(query);
    const emailMatch = student.personalInfo?.email?.toLowerCase().includes(query);
    
    return nameMatch || usnMatch || emailMatch;
  });

  const isContextSelected = programTypeId && programId && academicYearId && 
    ((selectedAcademicYear?.isSemesterBased && semesterId) || 
     (!selectedAcademicYear?.isSemesterBased && termId));

  // ============================================
  // STUDENTS CONTENT COMPONENT
  // ============================================
  const StudentsContent = () => {
    if (!tenantId || loadingFilters) {
      return <StudentsLoading />;
    }

    if (filterError) {
      return (
        <div className="p-4 md:p-6 bg-slate-50 min-h-screen font-sans text-slate-900 max-w-7xl mx-auto w-full">
          <div className="w-full rounded-xl border border-rose-200 bg-rose-50 p-6 text-center max-w-md mx-auto mt-6 shadow-sm">
            <div className="flex flex-col items-center gap-3">
              <div className="h-12 w-12 rounded-xl bg-rose-100 flex items-center justify-center border border-rose-200">
                <svg className="h-5 w-5 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <p className="text-sm font-bold text-rose-800">{filterError}</p>
              <p className="text-xs text-rose-500">Please check your connection and try again.</p>
              <button 
                onClick={() => window.location.reload()}
                className="mt-3 rounded-lg bg-rose-600 px-5 py-2 text-xs font-semibold text-white hover:bg-rose-700 shadow transition-all w-full"
              >
                Refresh Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="p-4 md:p-6 bg-slate-50 min-h-screen font-sans text-slate-900 max-w-7xl mx-auto w-full space-y-5">
        
        {/* Top Section */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-2">
          <div>
            <h1 className="text-xl font-black tracking-tight text-slate-900 sm:text-2xl flex items-center gap-2">
              <span>👥</span> Student Management
            </h1>
            <p className="text-xs text-slate-500 mt-0.5">View and manage student details and rules performance status</p>
          </div>
        </div>

        {/* ============================================ */}
        {/* FILTERS AND OPTIONS */}
        {/* ============================================ */}
        <div className="w-full rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="bg-slate-950 border-b border-slate-800 px-4 py-3.5 flex items-center gap-3">
            <div className="flex h-7 w-7 items-center justify-center rounded bg-blue-500/10 text-blue-400 border border-blue-500/20 text-xs shrink-0">
              🔍
            </div>
            <div>
              <h2 className="text-xs font-bold uppercase tracking-wider text-white">Select Academic Scope</h2>
              <p className="text-[11px] text-slate-400">Choose options below to load students</p>
            </div>
          </div>

          <div className="p-4">
            {error && (
              <div className="mb-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2.5">
                <span className="text-xs shrink-0">⚠️</span>
                <span className="text-xs font-semibold text-amber-900">{error}</span>
              </div>
            )}

            <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 lg:grid-cols-4">
              {/* PROGRAM TYPE */}
              <div className="space-y-1">
                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">
                  Program Category <span className="text-rose-500">*</span>
                </label>
                <select
                  value={programTypeId}
                  onChange={(e) => {
                    setProgramTypeId(e.target.value);
                    setProgramId('');
                    setAcademicYearId('');
                    setSemesterId('');
                    setTermId('');
                    setSearchQuery('');
                  }}
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium outline-none text-slate-800 h-9 shadow-sm"
                >
                  <option value="">Select Category</option>
                  {programTypes.map(pt => (
                    <option key={pt._id} value={pt._id}>{pt.name} ({pt.code})</option>
                  ))}
                </select>
              </div>

              {/* PROGRAM */}
              <div className="space-y-1">
                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">
                  Program <span className="text-rose-500">*</span>
                </label>
                <select
                  value={programId}
                  onChange={(e) => {
                    setProgramId(e.target.value);
                    setAcademicYearId('');
                    setSemesterId('');
                    setTermId('');
                    setSearchQuery('');
                  }}
                  disabled={!programTypeId}
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium outline-none text-slate-800 disabled:bg-slate-50 disabled:text-slate-400 h-9 shadow-sm"
                >
                  <option value="">Select Program</option>
                  {filteredPrograms.map(p => (
                    <option key={p._id} value={p._id}>{p.name} ({p.code})</option>
                  ))}
                </select>
              </div>

              {/* ACADEMIC YEAR */}
              <div className="space-y-1">
                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">
                  Academic Year <span className="text-rose-500">*</span>
                </label>
                <select
                  value={academicYearId}
                  onChange={(e) => {
                    const ayId = e.target.value;
                    setAcademicYearId(ayId);
                    setSemesterId('');
                    setTermId('');
                    setSearchQuery('');
                    const ay = academicYears.find(y => y._id === ayId);
                    setSelectedAcademicYear(ay);
                  }}
                  disabled={!programId}
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium outline-none text-slate-800 disabled:bg-slate-50 disabled:text-slate-400 h-9 shadow-sm"
                >
                  <option value="">Select Academic Year</option>
                  {academicYears.map(ay => (
                    <option key={ay._id} value={ay._id}>
                      {ay.name} {ay.status === 'active' ? '(Active)' : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* SEMESTER OR TERM */}
              {selectedAcademicYear?.isSemesterBased ? (
                <div className="space-y-1">
                  <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">
                    Semester <span className="text-rose-500">*</span>
                  </label>
                  <select
                    value={semesterId}
                    onChange={(e) => {
                      setSemesterId(e.target.value);
                      setSearchQuery('');
                    }}
                    disabled={!academicYearId || !selectedAcademicYear?.semesters}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium outline-none text-slate-800 disabled:bg-slate-50 disabled:text-slate-400 h-9 shadow-sm"
                  >
                    <option value="">Select Semester</option>
                    {selectedAcademicYear?.semesters?.map(sem => (
                      <option key={sem._id} value={sem._id}>
                        {sem.semesterName} ({sem.semesterType}) {sem.status === 'active' ? '✓' : ''}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="space-y-1">
                  <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">
                    Term <span className="text-rose-500">*</span>
                  </label>
                  <select
                    value={termId}
                    onChange={(e) => {
                      setTermId(e.target.value);
                      setSearchQuery('');
                    }}
                    disabled={!academicYearId || !selectedAcademicYear?.terms}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium outline-none text-slate-800 disabled:bg-slate-50 disabled:text-slate-400 h-9 shadow-sm"
                  >
                    <option value="">Select Term</option>
                    {selectedAcademicYear?.terms?.map(term => (
                      <option key={term._id} value={term._id}>
                        {term.termLabel || `Term ${term.termNumber}`} {term.status === 'active' ? '✓' : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* CONTEXT SUMMARY CARD */}
            {contextSummary && isContextSelected && (
              <div className="mt-4 pt-4 border-t border-slate-100 animate-in fade-in duration-300">
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-5 text-xs">
                    <div className="bg-white p-2 rounded-lg border border-slate-150">
                      <span className="text-slate-400 text-[10px] uppercase font-bold block">Category</span>
                      <p className="font-bold text-slate-800 mt-0.5 truncate">{contextSummary.programTypeName}</p>
                    </div>
                    <div className="bg-white p-2 rounded-lg border border-slate-150">
                      <span className="text-slate-400 text-[10px] uppercase font-bold block">Program</span>
                      <p className="font-bold text-slate-800 mt-0.5 truncate">{contextSummary.programName}</p>
                    </div>
                    <div className="bg-white p-2 rounded-lg border border-slate-150">
                      <span className="text-slate-400 text-[10px] uppercase font-bold block">Academic Session</span>
                      <p className="font-bold text-slate-800 mt-0.5 truncate">{contextSummary.academicYearName}</p>
                    </div>
                    <div className="bg-white p-2 rounded-lg border border-slate-150">
                      <span className="text-slate-400 text-[10px] uppercase font-bold block">{contextSummary.isSemesterBased ? 'Semester' : 'Term'}</span>
                      <p className="font-bold text-slate-800 mt-0.5">{contextSummary.termName || 'N/A'}</p>
                    </div>
                    <div className="bg-slate-900 text-white p-2 rounded-lg col-span-2 sm:col-span-4 lg:col-span-1 flex items-center justify-between lg:block shadow-sm">
                      <span className="text-slate-400 text-[10px] uppercase font-bold block">Total Class Size</span>
                      <p className="font-black text-sm lg:text-base text-white">{contextSummary.totalStudents} Students</p>
                    </div>
                  </div>
                  
                  <div className="mt-3 grid grid-cols-3 gap-3 text-center border-t border-slate-200 pt-3 text-xs">
                    <div className="bg-emerald-50/60 p-2 rounded-lg border border-emerald-100">
                      <span className="text-emerald-700 font-medium block text-[10px]">Eligible Count</span>
                      <p className="font-black text-base text-emerald-600 mt-0.5">{contextSummary.eligibleStudents || 0}</p>
                    </div>
                    <div className="bg-blue-50/60 p-2 rounded-lg border border-blue-100">
                      <span className="text-blue-700 font-medium block text-[10px]">Avg Attendance</span>
                      <p className="font-black text-base text-slate-800 mt-0.5">{contextSummary.averageAttendance || 0}%</p>
                    </div>
                    <div className="bg-purple-50/60 p-2 rounded-lg border border-purple-100">
                      <span className="text-purple-700 font-medium block text-[10px]">Total Subjects</span>
                      <p className="font-black text-base text-slate-800 mt-0.5">{contextSummary.totalSubjects || 0}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ============================================ */}
        {/* SEARCH AND STUDENTS LIST SECTION */}
        {/* ============================================ */}
        {isContextSelected && (
          <div className="mt-4 space-y-4 animate-in fade-in duration-200">
            
            {/* SEARCH COMPONENT INPUT BLOCK */}
            <div className="bg-white border border-slate-200 p-4 rounded-xl shadow-sm flex flex-col sm:flex-row gap-3 items-center justify-between">
              <div className="relative w-full sm:max-w-md">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-slate-400 text-sm">
                  🔍
                </span>
                <input
                  type="text"
                  placeholder="Search student by name, USN, or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-medium text-slate-800 placeholder-slate-400 focus:bg-white focus:border-indigo-500 focus:outline-none transition shadow-inner h-9"
                />
                {searchQuery && (
                  <button 
                    onClick={() => setSearchQuery('')}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-600 text-xs font-bold"
                  >
                    ✕
                  </button>
                )}
              </div>
              
              <div className="text-xs font-black uppercase tracking-wider text-slate-400 shrink-0 self-end sm:self-center">
                {loading ? (
                  <span className="text-[11px] text-slate-400 lowercase italic font-normal">Loading data...</span>
                ) : (
                  <span className="rounded-full bg-slate-200 px-2.5 py-1 text-[11px] font-bold text-slate-700 block">
                    {filteredStudents.length} of {pagination.total || students.length} Filtered Matches
                  </span>
                )}
              </div>
            </div>

            {loading ? (
              <div className="flex flex-col items-center justify-center py-12 bg-white rounded-xl border border-slate-200 shadow-sm">
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-indigo-600 border-t-transparent" />
                <p className="text-xs text-slate-400 mt-2">Fetching students...</p>
              </div>
            ) : filteredStudents.length === 0 ? (
              <div className="rounded-xl border border-slate-200 bg-white p-10 text-center shadow-sm">
                <p className="text-xs font-semibold text-slate-400">No matching student logs found.</p>
              </div>
            ) : (
              <>
                {/* Mobile Card Layout */}
                <div className="space-y-3 block md:hidden">
                  {filteredStudents.map((student, idx) => {
                    const attendance = student.overallStats?.overallAttendancePercentage || 0;
                    const isEligible = student.overallStats?.overallEligible || false;
                    const parsedPhotoUrl = parseProfilePhotoUrl(student.personalInfo);
                    
                    let avgGrade = 'F';
                    if (student.subjectPerformance?.length > 0) {
                      const validSubjects = student.subjectPerformance.filter(s => s.displayTotal?.maxMarks > 0);
                      if (validSubjects.length > 0) {
                        const totalPercentage = validSubjects.reduce((sum, sub) => sum + (sub.displayTotal?.percentage || 0), 0);
                        const avgPercentage = totalPercentage / validSubjects.length;
                        avgGrade = getGradeBadge(avgPercentage).label;
                      }
                    }

                    return (
                      <div key={idx} className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm space-y-3">
                        <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                          <div className="flex items-center gap-2.5">
                            <span className="text-slate-400 font-mono text-xs font-bold bg-slate-100 h-5 w-5 rounded flex items-center justify-center border border-slate-200">
                              {((currentPage - 1) * 20) + idx + 1}
                            </span>
                            {parsedPhotoUrl ? (
                              <img 
                                src={parsedPhotoUrl} 
                                alt=""
                                className="h-7 w-7 rounded-lg object-cover"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.parentElement.innerHTML = `<div class="h-7 w-7 rounded-lg bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-500 border border-slate-200">${student.personalInfo.fullName?.charAt(0) || '?'}</div>`;
                                }}
                              />
                            ) : (
                              <div className="h-7 w-7 rounded-lg bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-500 border border-slate-200">
                                {student.personalInfo.fullName?.charAt(0) || '?'}
                              </div>
                            )}
                            <div>
                              <h4 className="font-bold text-slate-900 text-sm leading-tight">{student.personalInfo.fullName}</h4>
                              <span className="text-[11px] font-mono text-slate-400">{student.personalInfo.usn}</span>
                            </div>
                          </div>
                          <span className={`px-2 py-0.5 text-[10px] font-bold rounded border ${isEligible ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-rose-50 text-rose-700 border-rose-200'}`}>
                            {isEligible ? 'Eligible' : 'Not Eligible'}
                          </span>
                        </div>

                        <div className="space-y-2 text-xs">
                          <div className="flex justify-between items-center bg-slate-50/50 p-2 rounded border border-slate-100">
                            <span className="text-slate-400 text-[10px] uppercase font-bold">Email Address:</span>
                            <span className="font-medium text-slate-700 truncate max-w-[220px] font-mono">{student.personalInfo.email || 'N/A'}</span>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-2 text-center text-xs bg-slate-50 p-2 rounded-lg border border-slate-100">
                            <div>
                              <span className="text-[10px] text-slate-400 block">Attendance Avg</span>
                              <span className={`font-bold ${attendance >= 75 ? 'text-emerald-600' : attendance >= 50 ? 'text-amber-600' : 'text-rose-600'}`}>
                                {attendance.toFixed(1)}%
                              </span>
                            </div>
                            <div>
                              <span className="text-[10px] text-slate-400 block">Grade Avg</span>
                              <span className="font-bold text-indigo-600 uppercase">{avgGrade}</span>
                            </div>
                          </div>
                        </div>

                        <button
                          onClick={() => handleViewStudent(student)}
                          className="w-full bg-slate-900 text-white font-semibold text-xs py-2 rounded-lg text-center hover:bg-slate-800 transition shadow-sm"
                        >
                          Open Performance File
                        </button>
                      </div>
                    );
                  })}
                </div>

                {/* Desktop View Table */}
                <div className="hidden md:block overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                  <table className="w-full text-left text-xs sm:text-sm">
                    <thead>
                      <tr className="bg-slate-950 text-white border-b border-slate-800">
                        <th className="px-4 py-3 font-semibold text-xs uppercase tracking-wider w-12 text-center">#</th>
                        <th className="px-4 py-3 font-semibold text-xs uppercase tracking-wider">USN</th>
                        <th className="px-4 py-3 font-semibold text-xs uppercase tracking-wider">Student Name</th>
                        <th className="px-4 py-3 font-semibold text-xs uppercase tracking-wider">Email Address</th>
                        <th className="px-4 py-3 font-semibold text-xs uppercase tracking-wider text-center">Attendance Avg</th>
                        <th className="px-4 py-3 font-semibold text-xs uppercase tracking-wider text-center">Overall Grade</th>
                        <th className="px-4 py-3 font-semibold text-xs uppercase tracking-wider text-center">Eligibility Status</th>
                        <th className="px-4 py-3 font-semibold text-xs uppercase tracking-wider text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {filteredStudents.map((student, idx) => {
                        const attendance = student.overallStats?.overallAttendancePercentage || 0;
                        const isEligible = student.overallStats?.overallEligible || false;
                        const parsedPhotoUrl = parseProfilePhotoUrl(student.personalInfo);
                        
                        let avgGrade = 'F';
                        if (student.subjectPerformance?.length > 0) {
                          const validSubjects = student.subjectPerformance.filter(s => s.displayTotal?.maxMarks > 0);
                          if (validSubjects.length > 0) {
                            const totalPercentage = validSubjects.reduce((sum, sub) => sum + (sub.displayTotal?.percentage || 0), 0);
                            const avgPercentage = totalPercentage / validSubjects.length;
                            avgGrade = getGradeBadge(avgPercentage).label;
                          }
                        }

                        const gradeColors = {
                          'O': 'bg-purple-50 text-purple-700 border-purple-200',
                          'A+': 'bg-blue-50 text-blue-700 border-blue-200',
                          'A': 'bg-emerald-50 text-emerald-700 border-emerald-200',
                          'B+': 'bg-teal-50 text-teal-700 border-teal-200',
                          'B': 'bg-amber-50 text-amber-700 border-amber-200',
                          'C': 'bg-orange-50 text-orange-700 border-orange-200',
                          'P': 'bg-yellow-50 text-yellow-700 border-yellow-200',
                          'F': 'bg-rose-50 text-rose-700 border-rose-200'
                        };

                        return (
                          <tr key={idx} className="hover:bg-slate-50 transition-colors">
                            <td className="px-4 py-3.5 text-slate-400 font-medium text-center">
                              {((currentPage - 1) * 20) + idx + 1}
                            </td>
                            <td className="px-4 py-3.5 font-mono font-bold text-slate-700 text-xs">
                              {student.personalInfo.usn}
                            </td>
                            <td className="px-4 py-3.5">
                              <div className="flex items-center gap-3">
                                {parsedPhotoUrl ? (
                                  <img 
                                    src={parsedPhotoUrl} 
                                    alt=""
                                    className="h-6 w-6 rounded-md object-cover"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.parentElement.innerHTML = `<div class="h-6 w-6 rounded-md bg-slate-100 flex items-center justify-center text-[10px] font-bold text-slate-500 border border-slate-200">${student.personalInfo.fullName?.charAt(0) || '?'}</div>`;
                                    }}
                                  />
                                ) : (
                                  <div className="h-6 w-6 rounded-md bg-slate-100 flex items-center justify-center text-[10px] font-bold text-slate-500 border border-slate-200">
                                    {student.personalInfo.fullName?.charAt(0) || '?'}
                                  </div>
                                )}
                                <span className="font-semibold text-slate-900">{student.personalInfo.fullName}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3.5 text-slate-600 font-mono text-xs">
                              {student.personalInfo.email || 'N/A'}
                            </td>
                            <td className="px-4 py-3.5 text-center">
                              <span className={getPercentageColor(attendance)}>
                                {attendance.toFixed(1)}%
                              </span>
                            </td>
                            <td className="px-4 py-3.5 text-center">
                              <span className={`inline-block px-2 py-0.5 text-xs font-bold rounded-md border ${gradeColors[avgGrade] || gradeColors['F']}`}>
                                {avgGrade}
                              </span>
                            </td>
                            <td className="px-4 py-3.5 text-center">
                              <span className={`inline-block px-2.5 py-0.5 text-xs font-semibold rounded-md border ${isEligible ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-rose-50 text-rose-700 border-rose-200'}`}>
                                {isEligible ? '✅ Eligible' : '❌ Not Eligible'}
                              </span>
                            </td>
                            <td className="px-4 py-3.5 text-center">
                              <button
                                onClick={() => handleViewStudent(student)}
                                className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 text-white px-3 py-1.5 text-xs font-semibold hover:bg-slate-800 transition shadow-sm"
                              >
                                Inspect File
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination Section */}
                {pagination.pages > 1 && !searchQuery && (
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-4 bg-white p-3 rounded-xl border border-slate-200 shadow-sm text-xs">
                    <div className="text-slate-500 font-medium">
                      Showing <strong className="text-slate-800">{((currentPage - 1) * pagination.limit) + 1}</strong> to <strong className="text-slate-800">{Math.min(currentPage * pagination.limit, pagination.total)}</strong> of <strong className="text-slate-800">{pagination.total}</strong> students
                    </div>
                    <div className="flex gap-1.5 w-full sm:w-auto justify-end">
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="flex-1 sm:flex-none px-3 py-2 font-semibold rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                      >
                        Previous
                      </button>
                      <span className="px-3.5 py-2 font-bold rounded-lg bg-slate-900 text-white shadow-sm">
                        {currentPage}
                      </span>
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === pagination.pages}
                        className="flex-1 sm:flex-none px-3 py-2 font-semibold rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* STUDENT DETAILS MODAL CONTAINER */}
        {isModalOpen && selectedStudent && (
          <StudentDetailsModal 
            student={selectedStudent} 
            onClose={handleCloseModal} 
          />
        )}

        {/* Background loading overlay */}
        {studentDetailsLoading && (
          <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-slate-900/40 backdrop-blur-xs">
            <div className="bg-white p-4 rounded-xl shadow-lg border border-slate-200 flex flex-col items-center gap-2">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-indigo-600 border-t-transparent" />
              <span className="text-xs font-bold text-slate-700">Loading details file...</span>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Wrap everything in Layout
  return (
    <Layout>
      <StudentsContent />
    </Layout>
  );
};

export default Students;